import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';

import {
  getInstrumentRatingNewsAction,
  getInstrumentResearchAction,
} from 'store/instrument/instrumentActions';
import { useArtifacts } from 'shared/hooks';
import { Artifacts, TabsPanel } from 'ui';
import { companyInstrumentTabs } from 'shared/constants/company';

const useStyles = makeStyles((theme) => ({
  container: {
    margin: theme.spacing(2, 0, 0),
  },
}));

export const Research = () => {
  const instrument = useSelector((state) => state.instrument);
  const { research, isResearchPending, isRatingRequestPending, ratingRequestError } = instrument;
  const { data, total } = research;

  const {
    limit,
    handleChangeArtifactsPage,
    handleArtifactClick,
    currentPage,
    handleChangeRowsPerPage,
  } = useArtifacts({
    query: instrument.data.name,
    getArtifactsAction: getInstrumentResearchAction,
    getArtifactsDataActionParams: {
      industryCode: instrument.data.industryCode,
    },
    customLimit: 10,
  });

  return (
    <Box p={2}>
      <Artifacts
        data={data}
        isPending={isResearchPending || isRatingRequestPending}
        total={total}
        limit={limit}
        onItemClick={handleArtifactClick}
        onChangePage={handleChangeArtifactsPage}
        ratingRequestError={ratingRequestError}
        currentPage={currentPage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export const RatingNews = () => {
  const instrument = useSelector((state) => state.instrument);
  const {
    ratingNews,
    isRatingNewsPending,
    isRatingRequestPending,
    ratingRequestError,
    data: { entityId },
  } = instrument;
  const { data, total } = ratingNews;

  const {
    limit,
    handleChangeArtifactsPage,
    handleArtifactClick,
    currentPage,
    handleChangeRowsPerPage,
  } = useArtifacts({
    query: instrument.data.name,
    getArtifactsAction: getInstrumentRatingNewsAction,
    getArtifactsDataActionParams: { entityId },
    customLimit: 10,
  });

  return (
    <Box p={2}>
      <Artifacts
        data={data}
        isPending={isRatingNewsPending || isRatingRequestPending}
        total={total}
        limit={limit}
        onItemClick={handleArtifactClick}
        onChangePage={handleChangeArtifactsPage}
        ratingRequestError={ratingRequestError}
        currentPage={currentPage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export const Tabs = ({ active, onChange }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const instrument = useSelector((state) => state.instrument);
  const { ratingNews, research } = instrument;
  const { total: ratingNewsTotal } = ratingNews;
  const { total: researchTotal } = research;

  return (
    <Paper variant="elevation" elevation={3} className={classes.container}>
      <TabsPanel active={active} onChange={onChange} lazy={false}>
        <div
          label={t(companyInstrumentTabs[0].label)}
          value={companyInstrumentTabs[0].value}
          counter={isNil(ratingNewsTotal) ? null : ratingNewsTotal}
        >
          <RatingNews />
        </div>
        <div
          label={t(companyInstrumentTabs[1].label)}
          value={companyInstrumentTabs[1].value}
          counter={isNil(researchTotal) ? null : researchTotal}
        >
          <Research />
        </div>
      </TabsPanel>
    </Paper>
  );
};

Tabs.defaultProps = {
  onChange: undefined,
};

Tabs.propTypes = {
  active: PropTypes.string.isRequired,
  onChange: PropTypes.func,
};
